
import { Language } from '@/types/language';

/**
 * Determine text direction based on language
 * @param lang Language code
 * @returns Text direction ('ltr' or 'rtl')
 */
export const getTextDirection = (lang: Language): 'ltr' | 'rtl' => {
  return ['he', 'ar'].includes(lang) ? 'rtl' : 'ltr';
};

/**
 * Language mapping for common browser language variants
 */
const languageMapping: Record<string, Language> = {
  'en': 'en',
  'en-US': 'en',
  'en-GB': 'en',
  'en-CA': 'en',
  'en-AU': 'en',
  'he': 'he',
  'he-IL': 'he',
  'ar': 'ar',
  'ar-SA': 'ar',
  'ar-EG': 'ar',
  'ar-AE': 'ar',
  'fr': 'fr',
  'fr-FR': 'fr',
  'fr-CA': 'fr',
  'fr-BE': 'fr',
  'es': 'es',
  'es-ES': 'es',
  'es-MX': 'es',
  'es-AR': 'es',
  'es-CO': 'es'
};

/**
 * Get browser's preferred language that matches available app languages
 * @param availableLanguages Array of language codes available in the app
 * @returns Preferred language code or null if no match found
 */
export const getBrowserPreferredLanguage = (availableLanguages: string[]): Language | null => {
  try {
    // Get browser languages in order of preference
    const browserLanguages = navigator.languages || [navigator.language];
    
    console.log('Browser languages:', browserLanguages);
    console.log('Available app languages:', availableLanguages);
    
    // Check each browser language against available languages
    for (const browserLang of browserLanguages) {
      const normalizedLang = browserLang.toLowerCase();
      
      // Direct match with language mapping
      if (languageMapping[normalizedLang]) {
        const mappedLang = languageMapping[normalizedLang];
        if (availableLanguages.includes(mappedLang)) {
          console.log(`Browser language match found: ${browserLang} → ${mappedLang}`);
          return mappedLang;
        }
      }
      
      // Try just the language code (first 2 characters)
      const shortLang = normalizedLang.substring(0, 2) as Language;
      if (availableLanguages.includes(shortLang) && ['en', 'he', 'ar', 'fr', 'es'].includes(shortLang)) {
        console.log(`Browser language short match found: ${browserLang} → ${shortLang}`);
        return shortLang;
      }
    }
    
    console.log('No browser language match found');
    return null;
  } catch (error) {
    console.warn('Error detecting browser language:', error);
    return null;
  }
};

/**
 * Get saved language from localStorage, browser preference, or default to English
 * @param availableLanguages Array of language codes available in the app (optional for backwards compatibility)
 * @returns The preferred language code
 */
export const getSavedLanguage = (availableLanguages?: string[]): Language => {
  // First, check if user has a saved preference
  const saved = localStorage.getItem('museum-language');
  if (saved && ['en', 'he', 'ar', 'fr', 'es'].includes(saved)) {
    console.log('Using saved language preference:', saved);
    return saved as Language;
  }
  
  // If no saved preference and we have available languages, try browser detection
  if (availableLanguages && availableLanguages.length > 0) {
    const browserLang = getBrowserPreferredLanguage(availableLanguages);
    if (browserLang) {
      console.log('Using browser detected language:', browserLang);
      return browserLang;
    }
  }
  
  // Fall back to English
  console.log('Using default language: en');
  return 'en';
};
